/**
 * 需要想定 daily 編集画面。
 *
 * URL: /demand/forecast-daily?workflowId=<id>&area=<areaCode>
 * (旧 URL /demand/forecast-daily/:section は query を保ったまま本 URL へ redirect する。routes/index.tsx)
 * 計画断面 (表示・GC カウントダウン) は wfCase.targetDate と今日 (JST) の差で判定し
 * (AC-DFD-000)、API に送る section は保存キー維持のためケースの断面 wfCase.section。
 *
 * 主要機能:
 * - 対象切替 (BG全体 read-only / 小売電気事業者) + 小売 dropdown。業務エリアは
 *   caseId (ケース) に固定 (design L1764-1770 準拠で Toolbar の select は非表示、
 *   URL の ?area= はケースのエリアと一致する時のみ初期値解決に使う)
 * - 48コマ編集グリッド (時刻/需要想定/差(保存済みとの差分、3断面共通)/
 *   当日実績/実績との差分/過去実績の列構成、spec AC-DFD-001、#3301)、
 *   明示保存ボタン (dirty な小売分をまとめて SaveDemandForecastDraft、離脱ガード
 *   経由の保存も同じ経路。旧自動保存 debounce は撤去済み、
 *   useForecastDraftEditor.ts ヘッダ参照)
 * - GCロック: gc_koma 以下のコマは disabled + warning 系配色 (design L209 準拠)
 * - 現在コマハイライト: current_koma
 * - 確定ボタン + 確定ダイアログ (gate: in-flight or 失敗小売 0 件)
 * - 多系列チャート: 当日実績エリア・トレンド予測線・気温/露点第2軸
 * - トレンド反映: gate を trendPreview 有無に、per-retailer avgRatio 適用
 *   (バッファ更新のみ、保存は明示保存ボタン経由)
 * - 類似日ダイアログ: lookback_days 入力、最低気温/最小需要列、祝日名graceful省略、
 *   「適用」時に RecordSimilarDayPicks を fire-and-forget 記録 (AC-14)
 * - §5 確定履歴から取込: BG全体ビューも取込可 (AC-08、旧制約解除。ADR
 *   demand-forecast-bg-import-per-retailer-direct / -scope-scale-confirmed-share-basis)。
 *   §6 簡易予測一括取込は小売電気事業者表示限定 (AC-DFD-033/033a、BG全体ビューは
 *   常時 disable + hover tooltip、2026-08-12 PdM 判断 #5320 で要件外化)
 * - 楽観ロック round-trip (draftUpdatedAt を proto Timestamp のまま保持)
 * - 衝突 (Aborted): 最新再取得+通知 (conflictNotice)。BG取込の衝突も同一 toast (AC-16)
 * - caseId 無し直接アクセスは一覧へリダイレクト
 * - 完了ケースで編集/確定無効化
 * - BG 全体ビュー + 配下小売 1 件のみ (AC-004c、spec 準拠調整 demand-forecast-
 *   daily-spec-align): isSingleRetailerBg を導出し、直接編集・チャートドラッグ・
 *   最大電力補正・確定履歴取込を許可する (BG 合計 = 唯一の小売値)。ただし
 *   簡易予測一括取込は AC-DFD-033/033a (2026-08-12 PdM 判断 #5320) により
 *   BG 全体表示 = 常時 disable で統一するため、単一小売 BG でも非活性となる
 *   (targetScope から局所派生する isBgTotalScope に isSingleRetailerBg の
 *   例外読み替えを入れていない = spec 側で単一小売例外を書いていないため
 *   保守的に spec 通り。単一小売 BG も許可する意図なら PdM 確認要)。
 *   配下小売が複数のときは KomaCard 側にインフォメッセージを出し小売選択を促す
 *   (AC-004d)
 * - 予測プロバイダ切替チップ / プロバイダ予測列 / プロバイダ比較系列は spec 準拠
 *   調整 (AC-07/08/09) で削除済み。backend/proto の `available_providers` /
 *   `provider_forecasts` / `selected_providers` は #4072 で撤去済み
 *   (provider stub 撤去 — Phase 2 外部プロバイダ契約は別 map で再設計)
 * - WorkflowBackBar は本画面には**統合しない** (design SoT):
 *   design/js/workflow-back-bar.js L5-6「forecast-today 等は専用バーを持つため、
 *   そこには読み込ませない」。ワークフロー文脈の表示と戻り導線は専用バー
 *   (SectionHeader のワークフロー ID リンク) が担う。spec workflow.md AC-011 の
 *   一般表現 (workflowId パラメータ付き画面に表示) とは衝突しており spec 側の
 *   追従は PdM 確認待ち (TC-R-01.1 テストが非表示を固定)
 */
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Code } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WorkflowSection } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import { DateSchema } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'

import { Stack } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowNotFoundView } from '~/components/common/WorkflowNotFoundView'
import { useJstMidnightTick } from '~/hooks/useJstMidnightTick'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { convertAreaLabel } from '~/models/area'
import { protoDateToCivilDate } from '~/models/civilDate'
import {
  formatDateTimeFull,
  isTargetDateAtOrAfterDayAfterTomorrowInJst,
  isTargetDateBeforeTodayInJst,
  isTargetDateTodayInJst,
  isTargetDateTomorrowInJst,
} from '~/models/dateTime'
import { gateCloseFrontierFromProto } from '~/models/gateClose'
import { isDemandForecastReadonly } from '~/models/workflowCaseReadonly'
import { AreaNotSetView } from '~/pages/DemandForecastDaily/components/AreaNotSetView'
import { ClientUpdateRequiredView } from '~/pages/DemandForecastDaily/components/ClientUpdateRequiredView'
import { ConfirmBar } from '~/pages/DemandForecastDaily/components/ConfirmBar'
import { ConfirmDemandDialog } from '~/pages/DemandForecastDaily/components/ConfirmDemandDialog'
import { ConfirmRecordImportDialog } from '~/pages/DemandForecastDaily/components/ConfirmRecordImportDialog'
import { ConfirmResultDialog } from '~/pages/DemandForecastDaily/components/ConfirmResultDialog'
import { ConfirmedRationaleCard } from '~/pages/DemandForecastDaily/components/ConfirmedRationaleCard'
import { DemandForecastDailyLayout } from '~/pages/DemandForecastDaily/components/DemandForecastDailyLayout'
import { DemandForecastWorkspace } from '~/pages/DemandForecastDaily/components/DemandForecastWorkspace'
import { GcCountdown } from '~/pages/DemandForecastDaily/components/GcCountdown'
import { ImportConfirmDialog } from '~/pages/DemandForecastDaily/components/ImportConfirmDialog'
import { InvalidWorkflowIdView } from '~/pages/DemandForecastDaily/components/InvalidWorkflowIdView'
import { RescaleModal } from '~/pages/DemandForecastDaily/components/RescaleModal'
import { SectionHeader } from '~/pages/DemandForecastDaily/components/SectionHeader'
import { SimilarDayDialog } from '~/pages/DemandForecastDaily/components/SimilarDayDialog'
import { SimpleForecastSettingsDialog } from '~/pages/DemandForecastDaily/components/SimpleForecastSettingsDialog'
import { Toolbar } from '~/pages/DemandForecastDaily/components/Toolbar'
import { TrendReflectDialog } from '~/pages/DemandForecastDaily/components/TrendReflectDialog'
import { UnattributedGuardDialog } from '~/pages/DemandForecastDaily/components/UnattributedGuardDialog'
import { UnsavedLeaveDialog } from '~/pages/DemandForecastDaily/components/UnsavedLeaveDialog'
import { useConfirmRecordImportFlow } from '~/pages/DemandForecastDaily/hooks/useConfirmRecordImportFlow'
import { useDemandForecastGridData } from '~/pages/DemandForecastDaily/hooks/useDemandForecastGridData'
import { useForecastPageModel } from '~/pages/DemandForecastDaily/hooks/useForecastPageModel'
import { useGetDemandForecastDaily } from '~/pages/DemandForecastDaily/hooks/useGetDemandForecastDaily'
import { useSimilarDayFlow } from '~/pages/DemandForecastDaily/hooks/useSimilarDayFlow'
import { useSimpleForecastImportFlow } from '~/pages/DemandForecastDaily/hooks/useSimpleForecastImportFlow'
import { useTrendReflectDialogPreview } from '~/pages/DemandForecastDaily/hooks/useTrendReflectDialogPreview'
import { useTrendReflectionWindow } from '~/pages/DemandForecastDaily/hooks/useTrendReflectionWindow'
import { useWeatherPointOverride } from '~/pages/DemandForecastDaily/hooks/useWeatherPointOverride'
import {
  CONFIRMED_UNATTRIBUTED_ADOPT_SLOTS,
  pastActualUnattributedAdoptSlots,
  splitAggregateAdoptTargets,
} from '~/pages/DemandForecastDaily/models/aggregateAdoptTargets'
import { formatBreakdownNames } from '~/pages/DemandForecastDaily/models/aggregateEdit'
import { confirmedMonthDayFromLabel } from '~/pages/DemandForecastDaily/models/confirmRecordRef'
import { shouldShowBeforeVpfdDailyNotice } from '~/pages/DemandForecastDaily/models/dailyWeatherSource'
import type { ForecastReferenceColumnValueKind } from '~/pages/DemandForecastDaily/models/forecastSectionView'
import { buildForecastSectionView } from '~/pages/DemandForecastDaily/models/forecastSectionView'
import {
  dayOfWeekLabelJa,
  parseAreaCodeParam,
  resolveSectionSegmentByTargetDate,
} from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { resolveGetDetailedErrorMessage } from '~/pages/DemandForecastDaily/models/serverError'
import { protoSlotsToKoma } from '~/pages/DemandForecastDaily/models/slotHelpers'
import { isDemandForecastSubStepNotReached } from '~/pages/DemandForecastDaily/models/subStepReachedGate'
import type { AdjustmentDisabledReasonKey } from '~/pages/DemandForecastDaily/models/toolbarGuards'
import { computeTrendTotals } from '~/pages/DemandForecastDaily/models/trendTotals'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'
import { useDemandForecastLegendHiddenKeys } from '~/providers/DemandForecastLegendProvider'
import { routePaths } from '~/routes/routePaths'

/**
 * `toolbarGuards.adjustmentDisabledReasonKey` の理由キー → i18n key。
 * 「調整」ボタンの disabled tooltip 文言 (デザイナー指摘 2026-08-13)。
 */
const ADJUSTMENT_DISABLED_REASON_I18N_KEYS: Record<
  AdjustmentDisabledReasonKey,
  string
> = {
  pastDate: 'page:demandForecastDaily.toolbar.adjustmentDisabledPastDate',
  completed: 'page:demandForecastDaily.toolbar.adjustmentDisabledCompleted',
  allGcLocked: 'page:demandForecastDaily.toolbar.adjustmentDisabledAllGcLocked',
  unattributedRemaining:
    'page:demandForecastDaily.confirm.serverError.reason.unattributedRemaining',
  noRetailer: 'page:demandForecastDaily.toolbar.adjustmentDisabledNoRetailer',
}

export const DemandForecastDailyPage = (): React.ReactElement => {
  const [simpleForecastGapNotice, setSimpleForecastGapNotice] = useState<
    | {
        kind: 'simple' | ForecastReferenceColumnValueKind
        retailerNames: string[]
        sourceDates: string[]
      }
    | undefined
  >(undefined)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // URL パラメータ解決。 caseId は ?workflowId=<UUID> から取得 (= 一覧 ForecastTable が付与)。
  // caseId 解決 + ケース情報取得 (read-only 状態チェック + target_date / area_code
  // 解決) は useCurrentWorkflowCase に委譲する。isReadonly は WF 全体 read-only の
  // fail-closed 判定 (取得失敗時も readonly 側に倒る) として利用し、本画面専用の
  // isDemandForecastReadonly を wfCase から追加で OR して isCompleted を確定する。
  const { caseId, wfCase, isReadonly, isWorkflowNotFound } =
    useCurrentWorkflowCase()
  const areaParam = searchParams.get('area')
  // API (Get / Save / Confirm / Preview*) に送る section は保存キー維持のため
  // ケースの断面 (wfCase.section)。URL の断面セグメントは使わない (AC-DFD-000)。
  // wfCase の読込前は targetDate / areaCode が未定で fetch は無効のまま。
  const section = wfCase?.section ?? WorkflowSection.UNSPECIFIED
  // 計画断面 (表示ラベル・パンくず・GC カウントダウン・気象地点 override の単位) は
  // 対象日と今日 (JST) の差で判定する (AC-DFD-000)。
  // 過去日・明後日より先・ケース読込前は undefined (計画断面を出さない)。
  const sectionSegment = resolveSectionSegmentByTargetDate(wfCase?.targetDate)

  // caseId 無しは一覧へリダイレクト
  useEffect(() => {
    if (caseId === undefined) {
      void navigate(routePaths.demandForecastList.path, { replace: true })
    }
  }, [caseId, navigate])

  // spec AC-DFC-007 (demand-fix-readonly-trigger design): 需要想定 step が
  // 確定されたタイミング以降、後続 step / WF 全体完了を問わず読み取り専用になる。
  // isDemandForecastReadonly は既存の WF 全体 COMPLETE/DELETED 判定
  // (workflow-delete-transition design §2/§5「遷移先関連画面」read-only 規定、
  // AC-10) に `demand_step_confirmed` を OR する。変数名 `isCompleted` は
  // KomaCard / Toolbar 等の既存 prop 名と揃えるため維持するが、意味は
  // 「read-only (需要想定確定 または 完了 または 削除済み)」。
  // fail-closed 認可: WF ケース取得失敗中 (wfCase === undefined) に確定済みケースが
  // 編集可能扱いになるのを防ぐため、hook が返す isReadonly (caseId 付き取得失敗時に
  // true になる) を OR する。
  const isCompleted =
    isReadonly || (wfCase !== undefined && isDemandForecastReadonly(wfCase))
  // AC-DFC-004b (demand-confirm-step-guard-error-messages design Decision
  // #1): 需要想定サブステップがワークフロー上まだアクティブ化されていない
  // (NOT_STARTED) 間は確定ボタンを追加で disabled にし、hover tooltip で
  // 理由を示す (ConfirmBar 配線)。既取得の wfCase.big_steps[].sub_steps[]
  // を読むだけの thin read (isDemandForecastSubStepNotReached 参照)。
  const isDemandSubStepNotReached =
    wfCase !== undefined && isDemandForecastSubStepNotReached(wfCase)
  // post-spec-#1515: WorkflowCase は単一 area_code を持つ。 下流のエリア選択ロジック
  // (includes / [0] / length) を不変に保つため、 単一 area を長さ 0/1 の配列に正規化する
  // (UNSPECIFIED = 未設定扱いで空)。
  const caseAreaCodes = useMemo<AreaCode[]>(
    () =>
      wfCase?.areaCode !== undefined && wfCase.areaCode !== AreaCode.UNSPECIFIED
        ? [wfCase.areaCode]
        : [],
    [wfCase?.areaCode],
  )

  // エリア切替: URL の area → ケースの area_codes[0] → undefined
  const [selectedAreaCode, setSelectedAreaCode] = useState<
    AreaCode | undefined
  >(undefined)
  useEffect(() => {
    if (selectedAreaCode !== undefined) return
    const fromUrl = parseAreaCodeParam(areaParam)
    if (fromUrl !== undefined && caseAreaCodes.includes(fromUrl)) {
      setSelectedAreaCode(fromUrl)
    } else if (caseAreaCodes.length > 0) {
      setSelectedAreaCode(caseAreaCodes[0])
    }
  }, [areaParam, caseAreaCodes, selectedAreaCode])

  // target_date: ケースの target_date を使用
  const targetDate = wfCase?.targetDate

  // 気象観測地点の画面内一時切替 (weather-point-in-screen-temp-switch design,
  // AC-DFC-008a/008b)。sectionSegment (対象日で判定した計画断面) を渡し、断面切替時の
  // 明示 reset を hook 内に閉じ込める (design D4)。
  // isReadonly = isCompleted を渡すのは、同一 mount 内で確定 → isCompleted が
  // false → true に反転した瞬間に override を落として request key を組織共通
  // 設定に戻すため (design demand/forecast-today.html L3529-3531 / L3573-3574
  // 「確定完了で組織の設定に戻ります」/ 「[戻す] ボタンと確定完了時の自動復帰
  // が同じ関数を通る」)。D6 の select・状態行 hide だけでは request seam に
  // override が残り続け、確定断面に切替先地点の live 気象が乗る (PR #6000
  // review B1-3)。
  const weatherPointOverride = useWeatherPointOverride({
    areaCode: selectedAreaCode,
    sectionSegment,
    isReadonly: isCompleted,
  })

  // 類似日ダイアログで選んだ過去実績日付文字列 (YYYY-MM-DD[])。
  // ツールバーの「カレンダーで指定」導線 (＋追加ボタン + 日付 Input) は
  // デザイナー指摘 2026-08-14 で削除し、 手動指定は類似日ダイアログの
  // 「任意日追加」へ一本化した (design も 2026-07-15 に同じ集約を済ませている)。
  const [pastActualDateStrs, setPastActualDateStrs] = useState<string[]>([])

  // ツールバー chip の × (design L717 相当): 単日解除
  const handlePastActualDateRemove = useCallback((date: string): void => {
    setPastActualDateStrs((prev) => prev.filter((d) => d !== date))
  }, [])

  /**
   * ツールバー chip の日付を選び直す (デザイナー指摘 2026-09-14)。
   *
   * **位置を保って置換する。** チャートの系列色と 48 コマ表の過去実績列は配列順
   * (`past_0`..`past_4`) に対応しているため、 削除 + 末尾追加にすると日付を
   * 1 日ずらしただけで色と列順が入れ替わる。
   *
   * 既に選択済みの日付を選んだ場合は**重複させず元を取り除く**。 同じ日付が
   * 2 スロットに入ると React の key が衝突し、 同じ系列が二重に描かれる。
   */
  const handlePastActualDateChange = useCallback(
    (from: string, to: string): void => {
      setPastActualDateStrs((prev) => {
        if (!prev.includes(from)) return prev
        if (prev.includes(to)) return prev.filter((d) => d !== from)
        return prev.map((d) => (d === from ? to : d))
      })
    },
    [],
  )

  // ツールバー「クリア」(design L717): 選択済みスロットを全解除
  const handlePastActualDatesClear = useCallback((): void => {
    setPastActualDateStrs([])
  }, [])

  // YYYY-MM-DD 文字列 → ProtoDate 配列 (useGetDemandForecastDaily に渡す)
  const pastActualDates = useMemo(
    () =>
      pastActualDateStrs
        .map((s) => {
          const [y, m, d] = s.split('-').map(Number)
          if (y === undefined || m === undefined || d === undefined) return null
          return create(DateSchema, { year: y, month: m, day: d })
        })
        .filter((d) => d !== null),
    [pastActualDateStrs],
  )

  // GetDemandForecastDaily
  const {
    data: pageData,
    isLoading,
    isFetching: isPageFetching,
    isPlaceholderData,
    error: pageError,
    refetch: refetchPage,
  } = useGetDemandForecastDaily({
    caseId,
    areaCode: selectedAreaCode,
    section,
    targetDate,
    pastActualDates,
    locationName: weatherPointOverride.overrideLocationName,
    // 確定で override が落ちた瞬間に、直前の一時地点 response を placeholder として
    // 持ち越さない (hook の `isReadonly` JSDoc 参照)。
    isReadonly: isCompleted,
  })

  const page = pageData?.page

  /**
   * 直前の応答を placeholder として描いている最中か (#7454)。
   *
   * 気象観測地点の一時切替では **48 コマ値・需要チャートは据え置き、 気象だけを
   * 空にする**。 placeholder は「1 つ前の地点の応答」 なので、 気象をそのまま出すと
   * **切替先の地点名ラベルの下に前の地点の気象が並ぶ**。 需要値 (`retailers` /
   * `bgTotalSlots`) は地点 override と無関係に同じ値が返る (D2) ので据え置いてよい。
   *
   * 確定 (`demandStepConfirmed` false→true) では [[useWeatherPointOverride]] が
   * override を自動解除するが、 この時 D6 により一時切替 UI と「一時表示」 表記が
   * hide される。 気象を据え置くと**切替先の気象を確定時スナップショットとして
   * 見せてしまう** (AC-DFC-008c 違反、 PR #7803 レビュー指摘)。 表示側で空にすれば
   * この経路も塞がる。
   */
  const isWeatherStale = isPlaceholderData

  // auxiliary data の per-source status (G4: 外部依存失敗時の降格表示)。
  const weatherStatus = isWeatherStale ? undefined : pageData?.weatherStatus
  const pastActualsStatus = pageData?.pastActualsStatus

  // page の各種スロットを表示用 KomaSlots へ変換した派生データ (今日実績 / 気温 / 露点 /
  // 過去実績グリッド)。God Component 化回避のため hook に集約
  const {
    todayActualKomaSlots,
    temperatureForecastKomaSlots,
    temperatureActualKomaSlots,
    dewpointActualKomaSlots,
    pastActualGridData,
  } = useDemandForecastGridData({ page })

  // AC-04/AC-05 (spec AC-DFC-009 / grill Q2、demand-forecast-danmen-guard):
  // チャート外部凡例 (露点トグル含む) の hiddenKeys を App root の
  // DemandForecastLegendProvider に置くことで、別ケースへの遷移による再 mount
  // (List → Daily → 別ケースの Daily) を跨いで OFF 状態を保持する。
  // (index.tsx 内 useState では実運用の断面切替経路が Daily 画面を unmount する
  // ため保持されない — PR #4945 レビュー指摘。) ブラウザ再読込は App 自体の再
  // マウントで既定 ON (new Set()) に戻る (= spec の「既定 ON への復帰は
  // ブラウザ再読み込み時」を満たす。永続化はしない、Context Pack Constraints)。
  // 参照列の confirmRef_<id> hiddenKey は useConfirmRecordImportFlow に閉じ込めた
  // 連動除去 (remove / clear / case・エリア・対象日切替の reset) が捨てるため、
  // index 側は同期を持たない (setHiddenKeys は凡例トグル onHiddenKeysChange 用に
  // hook へ注入する)。
  const { hiddenKeys, setHiddenKeys } = useDemandForecastLegendHiddenKeys()

  // AC-15: 48コマテーブルの列 (時間帯) ホバー時にチャート上の対応ポイントを強調する
  const [highlightedKoma, setHighlightedKoma] = useState<number | undefined>(
    undefined,
  )
  // AC-DFD-006a-6: 48コマテーブルの参照列見出しにホバー中の系列 (チャートで強調・減光する)
  const [emphasizedSeriesKey, setEmphasizedSeriesKey] = useState<
    string | undefined
  >(undefined)

  // 対象日が JST の今日か。 ケースの断面 (WorkflowSection) ではなく wfCase.targetDate で
  // 判定する (backend valueobject.IsTargetDateToday との対応)。 これにより「翌日
  // workflow で target_date=today のケース」 でも GC・当日実績・実績トレンド反映
  // が正しく効く。
  const isTargetDateToday = isTargetDateTodayInJst(targetDate)
  // 対象日が JST の翌日か (VPFD 発表前の日別予報案内に使う)。 ケースの断面
  // (WorkflowSection) ではなく wfCase.targetDate で判定する。
  const isTargetDateTomorrow = isTargetDateTomorrowInJst(targetDate)
  const preTemperatureActualKomaSlots =
    page === undefined
      ? undefined
      : protoSlotsToKoma(page.preTemperatureActualSlots, 2)
  const preDewpointActualKomaSlots =
    page === undefined
      ? undefined
      : protoSlotsToKoma(page.preDewpointActualSlots, 2)
  // AC-WD-005e (Design Note tmp/designing-feature/demand-fix-daily.md
  // Decision #2): 気象粒度案内メッセージ (3時間ごとの天気アイコン・気温は
  // 翌日までの表示となる旨) の表示トリガー。ケースの断面 (WorkflowSection) ではなく
  // 対象日 (wfCase.targetDate) が JST の今日+2 以降かで判定する (上の
  // isTargetDateToday / isTargetDateTomorrow と同じ配線様式 — ケースの断面は
  // 雛形由来で、対象日オフセットと 1:1 対応しない)。
  const showThreeHourGranularityLimitNotice =
    isTargetDateAtOrAfterDayAfterTomorrowInJst(targetDate)
  // AC-WD-005e (翌日断面・05 時前): 表示中の予報が日別の源 (VPFD 発表前) で、
  // BG_TOTAL 業務確定済みでない時だけ案内する。確定済みは確定時の粒度のままなので
  // 出さない (AC-DFC-014)。確定済みの signal は backend が確定時 snapshot を返す
  // 条件と同じ wfCase.demandStepConfirmed (isCompleted は参照表示 / WF 完了でも
  // true になるため使わない)。判定は shouldShowBeforeVpfdDailyNotice。
  const showBeforeVpfdDailyNotice = shouldShowBeforeVpfdDailyNotice({
    isTargetDateTomorrow,
    weatherSource: isWeatherStale ? undefined : page?.weather?.weatherSource,
    isBgTotalConfirmed: wfCase?.demandStepConfirmed === true,
  })
  // conventions.md §GC ロック「過去日 → 全コマロック」「表示と実行の乖離は
  // 許容しない」(principles.md 鉄則 1)。過去日 write-gate の判定 (canMutate) と
  // write-seam の強制は useForecastPageModel に集約済み。canMutate は本画面では
  // model 内でのみ呼ばれ、index は model が返す canMutateTargetDate flag を表示 gate
  // に消費する (5 seam を単一 predicate 経由に統一)。
  // PR #5066 review C-7/C-8: 画面を開いたまま JST 0:00 を跨いだ直後、gate/表示を
  // 過去日へ即時追従させるため境界タイマーで 1 回だけ再レンダーを起こし (model 内の
  // canMutateTargetDate は毎レンダー実時刻で再評価される)、tick を契機に refetch して
  // backend 契約 (過去日 → gcKoma=48) を再取得する。
  const midnightTick = useJstMidnightTick()
  useEffect(() => {
    if (midnightTick === 0) return
    void refetchPage()
  }, [midnightTick, refetchPage])

  // gc_koma / current_koma は backend `GcKomaForDate` / `CurrentKomaOnDate` の
  // 契約 (koma_number.go) をそのまま採用する。過去日は Some(48) (全 GC 済み =
  // 全編集不可)、翌日/翌々日は None (=0、GC 概念未成立で全編集可)、当日は
  // GcKomaAt(now)。
  const gcKoma = page?.gcKoma ?? 0
  // page を作った時刻の GC 境界 (gc_koma と同じ時刻)。GC カウントダウンとチャートの
  // GC 縦線は、これを現在時刻まで進めて次の GC を求める (壁時計から再計算しない)。
  const gateCloseFrontier = useMemo(
    () => gateCloseFrontierFromProto(page?.gateCloseFrontier),
    [page],
  )
  // currentKoma は「今進行中のコマ」のマーカーなので当日以外は意味を持たない。
  // ここでは生値 (backend 契約) のまま保持し、「当日以外は 0」の gate は
  // buildForecastSectionView (models/forecastSectionView.ts) が単一箇所で行う
  // (呼び出し元でこの invariant を重複判定しない、下の useMemo 参照)。gate 前で
  // あることを名前に持たせ、gate 済み値は forecastSectionView.cells.currentKoma
  // から読む。
  const rawCurrentKoma = page?.currentKoma ?? 0

  // AC-004c/AC-004d (spec 準拠調整 demand-forecast-daily-spec-align): BG 全体
  // ビューの配下小売一覧。dialog flow hooks (§5/§6 取込) が消費するため index に残す。
  // useMemo で参照を安定させる (Decision 5/6: resolveAggregateTargetSlots の
  // useCallback 依存に含めるため、page?.retailers ?? [] を毎レンダー新規配列に
  // しない)。
  const retailers = useMemo(() => page?.retailers ?? [], [page])
  useEffect(() => {
    setSimpleForecastGapNotice(undefined)
  }, [caseId, selectedAreaCode, targetDate, section])

  // AC-DFC-002a (design note tmp/designing-feature/demand-bg-expired-
  // message.md): ケース自身の需要 BG が対象日時点で失効し有効小売が 0 件の
  // とき、page 応答の signal (case_bg_expired) をテーブル本体領域
  // (KomaCard) の空状態案内に変換する。signal は「失効」限定 (適用前は本
  // slice スコープ外、Decision #1) で、失効時は backend 契約上 retailers も
  // 必ず空になるが、AC 文の両条件 (BG 失効 かつ 有効小売 0 件) を frontend
  // 側でも明示的に併せて確認する (signal 単独を信頼しきらない防御)。
  const isBgExpiredNotice =
    page?.caseBgExpired === true && retailers.length === 0

  // ── page-model (gate / 過去日 write-seam / save-dirty / orchestration の集約) ──
  // 従来 index の render body に散っていた決定ロジックを deep な hook に集約する
  // (アーキ改善 C3)。editor / 離脱ガード / 確定 mutation は本 hook 配下に compose
  // される。canMutate はこの model 内でのみ呼ばれ、index は返り値の flag / guarded
  // action を消費するだけ (write-seam の同期を構造で保証、convention 依存を排除)。
  const serverConfirmedAt =
    wfCase?.demandStepConfirmed === true &&
    wfCase.demandConfirmedAt !== undefined
      ? timestampDate(wfCase.demandConfirmedAt)
      : undefined
  // AC-DFC-018 / 019: 根拠記録は需要想定の確定済み (AC-DFC-008c) の時だけ出す。
  // 確定済みなのに page が根拠記録を持たない時は読み出せなかった案内になるので、
  // 確定直後の page の取り直し中は出さない。
  const showConfirmedRationale =
    serverConfirmedAt !== undefined &&
    page !== undefined &&
    (page.confirmRationale !== undefined || !isPageFetching)
  const model = useForecastPageModel({
    page,
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    gcKoma,
    retailers,
    isCompleted,
    isConfirmedOnServer: serverConfirmedAt !== undefined,
    refetchPage,
  })
  const {
    // selection state
    targetScope,
    selectedRetailerBusinessCd,
    dragMode,
    setDragMode,
    onRetailerChange,
    onTargetScopeChange,
    // scope / gates
    // Decision 6 (design note tmp/designing-feature/demand-fix-aggregate-
    // reference-adopt.md): 集計スコープ (BG全体×配下複数) 判定の共有 SoT。
    isRetailerEditableTarget,
    canMutateTargetDate,
    canEditNow,
    isReadOnly,
    aggregateUnfilledRetailerNames,
    isAggregateLocked,
    canEditAggregate,
    canRescale,
    canDiscard,
    canImportToDraft,
    canImportForecast,
    komaBaselineSlots,
    currentPeakKwh,
    adjustmentDisabledReasonKey: adjustmentDisabledReasonKeyValue,
    effectiveDirtyKomas,
    effectiveSaveState,
    // 配下 editor result (nested value)。表示 state / buffer-write ハンドラは
    // ここから読む (page-model の素通し再エクスポートを廃止したため、editor の
    // shape は model.editor の 1 seam に閉じる)。
    editor,
    targetDateLabel,
    // write orchestration
    handleSave,
    handleSaveAndLeave,
    handleDiscardAndLeave,
    handleResolveConflict,
    handleConfirmExecute,
    // leave guard
    leaveGuardBlocked,
    leaveGuardCancel,
    isLeaveSaving,
    // confirm dialog + result
    isConfirmOpen,
    openConfirmDialog,
    closeConfirmDialog,
    isConfirming,
    confirmError,
    confirmResultAt,
    closeConfirmResult,
    confirmedIndicatorAt,
    isUnattributedGuardOpen,
    closeUnattributedGuard,
    // rescale modal
    isRescaleOpen,
    openRescale,
    closeRescale,
  } = model
  // 配下 editor result から表示 state / buffer-write ハンドラを取り出す
  // (view 配線 / dialog flow への入力)。
  const {
    editRows,
    displaySlots,
    displayLabel,
    currentEditRow,
    currentSaveErrorMessage,
    editRowList,
    anyDirty,
    hasInFlight,
    lastSavedAt,
    canConfirm,
    handleKomaChange,
    handlePaste,
    handleChartDragEdit,
    handleDiscardChanges,
    handleRescaleApply,
    handleTrendReflectApply,
    handleAdoptReferenceColumn,
    adoptedReferenceColumnKey,
    handleAdoptReferenceColumnForRetailers,
    resolveAggregateAdoptedKey,
    applyImportedDraft,
    applyPreviewSimpleForecastBgResults,
  } = editor

  // AC-DFD-050・051: 「確定済み ({確定日時})」は、確定済みである間は出し続ける
  // (確定直後だけでなく、画面を開き直した時も出す。#10963 追加 8-1)。確定済みの
  // 画面は wfCase の確定日時 (サーバーの記録) を使い、wfCase の反映前の確定直後だけ
  // 確定 RPC 応答の日時 (confirmedIndicatorAt) で補う。
  const confirmedHintAt = serverConfirmedAt ?? confirmedIndicatorAt
  // 「調整」disabled 理由キー → i18n 文言 (理由キー導出は model が持つ。ここは
  // presentation policy = キー→文言の写像のみ)。
  const adjustmentDisabledReason =
    adjustmentDisabledReasonKeyValue !== undefined
      ? t(
          ADJUSTMENT_DISABLED_REASON_I18N_KEYS[
            adjustmentDisabledReasonKeyValue
          ],
        )
      : undefined

  // ── §5 確定履歴から取込 → 参照列 / §6 簡易予測一括取込 ──────────────

  // --- §5 確定履歴から取込 → 参照列 (s07-confirm-history-import-reference-view.md) ---
  // 旧・書込み型 RPC と異なり draft (editRows) を一切変更しない
  // ため applyImportedDraft/applyImportedBgResults/refetchPage は渡さない。
  // BG_TOTAL scope の bg_retailer_business_cds は現在 case 配下の retailers から
  // 解決するため渡す。

  const confirmRecordImportFlow = useConfirmRecordImportFlow({
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    targetScope,
    selectedRetailerBusinessCd,
    bgCode: wfCase?.bgCode,
    retailers,
    // 参照列の削除/クリア/リセット時に対応する confirmRef_<id> hiddenKey を
    // flow 内で連動除去するため凡例 set-fn を注入する (codex review #5041 の
    // id 再一致 hide 復活 bug の防止をこの seam 1 点に閉じ込める、C2 refactor)。
    setLegendHiddenKeys: setHiddenKeys,
    // 過去日 gate (PR #5066 review C-6) はここでは不要:
    // §5 は「参照列」型 refactor 後で draft を一切変更しない (main で refactor
    // 済み)。取込 mutation を発火せず既存 confirmRecord を読み出して chip を
    // 追加するだけなので、書き込み経路の defense in depth 対象外。
  })
  // review repair (slice 2): useConfirmRecordImportFlow は毎レンダー新規
  // object を返すため、`confirmRecordImportFlow` 自体を useCallback/useMemo の
  // dep にすると常に不安定になる (deps が指す先が実質的に「常に新しい
  // 参照」になり、下流の resolveAggregateTargetSlots → aggregateAdoptTargetCounts
  // → forecastSectionView の useMemo が毎レンダー無効化される)。hook 内で
  // 既に useCallback 化済みの resolveBgResultsForRef (deps = confirmRecordRefs
  // state) を dep として使う。
  const { resolveBgResultsForRef } = confirmRecordImportFlow

  // Decision #5: ツールバー「クリア」は過去実績 + 確定履歴の参照列 state を
  // まとめて空にする (design pattern に合わせる、共通「クリア」ボタン領域)。
  // 参照列側の confirmRef_ hiddenKey 除去は handleConfirmRecordRefsClear が
  // 内部で連動する (codex review #5041 の hide 復活 bug 対策は flow hook に集約)。
  const handleClearReferenceColumns = useCallback((): void => {
    handlePastActualDatesClear()
    confirmRecordImportFlow.handleConfirmRecordRefsClear()
  }, [handlePastActualDatesClear, confirmRecordImportFlow])

  // design note tmp/designing-feature/demand-fix-aggregate-reference-adopt.md
  // Decision 6: 集計スコープ (BG全体×配下複数の集計表示) 判定は model の
  // isRetailerEditableTarget (forecastGates.ts の SoT) の否定を使う (需要グループ
  // 追加時にこの判定を通せば同経路が自動適用される、BG_TOTAL リテラル直書きを
  // 避ける)。
  // AC-DFD-047b / 047c (issue #11461): 小売 1 社 + 帰属不明残量ありの BG で帰属
  // 不明を 0 にする集計採用をすると、配下件数が 1 件に戻り編集可否は小売単独に
  // 切り替わる。その採用が続く間は集計採用として「採用中」を出し解除できるよう、
  // 採用状態の判定だけは集計採用の有無も見る (編集可否の gate は変えない)。
  const isAggregateScope =
    targetScope === 'BG_TOTAL' &&
    (!isRetailerEditableTarget || resolveAggregateAdoptedKey() !== undefined)

  // Decision 5: per-retailer データは KomaGrid に渡さない。column.key (=確定
  // レコード id) から confirmRecordImportFlow の bgResults を解決し、現在の BG
  // 配下 retailer に絞り込む (AC-DFD-032: 確定履歴時点に存在したが現在 BG 配下に
  // いない retailer は「解決対象」から除く。missing_retailer_business_cds は
  // 取込時に既に warning toast 済み)。採用コールバック (routing) と
  // adoptUnavailable 判定の両方がこの「解決対象」集合を共有する。確定時に値が
  // 無い現配下の小売の通知 (AC-DFD-048) は splitAggregateAdoptTargets が判定する。
  const resolveAggregateTargetSlots = useCallback(
    (
      confirmRecordId: string,
    ): Array<{ retailerBusinessCd: string; slots: KomaSlots }> | undefined => {
      const bgResults = resolveBgResultsForRef(confirmRecordId)
      if (bgResults === undefined) return undefined
      const currentRetailerBusinessCds = new Set(
        retailers.map((r) => r.retailerBusinessCd),
      )
      return bgResults
        .filter((r) => currentRetailerBusinessCds.has(r.retailerBusinessCd))
        .map((r) => ({
          retailerBusinessCd: r.retailerBusinessCd,
          slots: protoSlotsToKoma(r.forecastSlots),
        }))
    },
    [resolveBgResultsForRef, retailers],
  )

  // AC-DFD-047d (issue #7436): 集計スコープで過去実績列を採用する時の
  // per-retailer 値。48コマ表の列と同じ pastActualGridData (KomaSlots 変換・
  // M/D ラベル済み) から、各小売に帰属する実績 (retailerSlots) をそのまま使う
  // (エリア合計 slots を配分しない)。分解不能の日 (ownerBreakdownAvailable=
  // false) は採用ボタンが非活性 (selectPastActualColumn 'notBrokenDown') の
  // ため、ここでは防御的に undefined を返す。
  const resolveAggregatePastActual = useCallback(
    (columnKey: string) => {
      const column = pastActualGridData.find((pa) => pa.id === columnKey)
      if (column === undefined || !column.ownerBreakdownAvailable) {
        return undefined
      }
      return column
    },
    [pastActualGridData],
  )

  const handleAdoptReferenceColumnRouted = useCallback(
    (column: {
      key: string
      sourceLabel: string
      slots: KomaSlots
      valueKind: ForecastReferenceColumnValueKind
    }): void => {
      if (targetScope === 'UNATTRIBUTED') return
      if (!isAggregateScope) {
        const outcome = handleAdoptReferenceColumn(column)
        if (outcome !== 'noop') setSimpleForecastGapNotice(undefined)
        return
      }
      // AC-DFD-047d / 048 (issue #7436): 集計スコープでは、過去実績は各小売の
      // 帰属実績を、確定履歴は確定時の小売別の値を、それぞれの小売へそのまま
      // 入れる。undefined (分解不能の日 / 未知の key・RETAIL scope 追加の
      // 確定履歴) は防御的 no-op (どちらも集計スコープでは押せない契約)。
      // AC-DFD-047c / 047d (issue #11461): 帰属不明には、過去実績は `-UNK` の
      // 実績をそのまま (分解済みで `-UNK` が無い日は 0)、確定履歴は 0 を入れる
      // (確定値に帰属不明は無い、AC-DFD-058)。帰属不明が 0 なのは取り逃がしでは
      // ないため splitAggregateAdoptTargets (047e / 048 の通知) には渡さない。
      let resolved: Array<{ retailerBusinessCd: string; slots: KomaSlots }>
      let unattributedSlots: KomaSlots
      let missingLabel: string
      if (column.valueKind === 'actual') {
        const pastActual = resolveAggregatePastActual(column.key)
        if (pastActual === undefined) return
        resolved = pastActual.retailerSlots
        unattributedSlots = pastActualUnattributedAdoptSlots(
          pastActual.slots,
          pastActual.unattributedSlots,
        )
        // 通知文の {MM/dd} は列ヘッダーと同じ「M/D」表記。
        missingLabel = pastActual.label
      } else {
        const confirmed = resolveAggregateTargetSlots(column.key)
        if (confirmed === undefined) return
        resolved = confirmed
        unattributedSlots = CONFIRMED_UNATTRIBUTED_ADOPT_SLOTS
        // 確定日の月日 (列ヘッダーと同じ M/D)。「確定」の語は文言側に持つ。
        missingLabel =
          confirmedMonthDayFromLabel(column.sourceLabel) ?? column.sourceLabel
      }
      const { targets, missingDisplayNames } = splitAggregateAdoptTargets(
        retailers,
        resolved,
      )
      const outcome = handleAdoptReferenceColumnForRetailers({
        key: column.key,
        sourceLabel: column.sourceLabel,
        retailerSlots: targets,
        unattributedSlots,
      })
      // AC-DFD-047e / 048: 取れた内訳だけ反映して採用を完了させ、取れなかった
      // 小売を知らせる。全小売に値が無い日も帰属不明 (0 を含む) は反映するので
      // 採用は完了する (AC-DFD-047c)。帰属不明の buffer も無く取込対象 0 件で
      // noop の時も、押した結果が何も起きない理由として同じ通知を出す。解除 (unadopted) では
      // 出さない (採用中の列を解除した時に取込対象が 0 件になっていても同じ)。
      if (
        outcome === 'adopted' ||
        (outcome === 'noop' && targets.length === 0)
      ) {
        setSimpleForecastGapNotice(
          missingDisplayNames.length === 0
            ? undefined
            : {
                kind: column.valueKind,
                retailerNames: missingDisplayNames,
                sourceDates: [missingLabel],
              },
        )
      } else if (outcome === 'unadopted') {
        setSimpleForecastGapNotice(undefined)
      }
    },
    [
      isAggregateScope,
      targetScope,
      handleAdoptReferenceColumn,
      resolveAggregatePastActual,
      resolveAggregateTargetSlots,
      retailers,
      handleAdoptReferenceColumnForRetailers,
    ],
  )

  const handleDiscardChangesWithGapClear = useCallback((): void => {
    handleDiscardChanges()
    setSimpleForecastGapNotice(undefined)
  }, [handleDiscardChanges])

  const handleDiscardAndLeaveWithGapClear = useCallback((): void => {
    handleDiscardAndLeave()
    setSimpleForecastGapNotice(undefined)
  }, [handleDiscardAndLeave])

  // AC-DFD-047b: 「採用中」は採用した表示単位でだけ表示する。集計スコープは
  // 集計表示で採用した列 (その採用で書き込んだ小売全員が採用中の時のみ)、
  // RETAILER / 配下1件 BG は選択中小売で採用した列。
  const effectiveAdoptedReferenceColumnKey = isAggregateScope
    ? resolveAggregateAdoptedKey()
    : adoptedReferenceColumnKey

  // gate review (#6048): builder の adoptUnavailable 判定入力 (D7)。確定履歴
  // 参照列 id → 集計スコープでの per-retailer 解決対象数 (現在の BG 配下に
  // いる小売の数)。0 件 (または未解決) の列は adoptUnavailable=true になる
  // (AC-DFD-032)。builder へ関数を渡して閉包越しに state を引かせない
  // (builder は data → data の pure builder のまま)。非集計スコープでは空 Map。
  const aggregateAdoptTargetCounts = useMemo((): ReadonlyMap<
    string,
    number
  > => {
    const counts = new Map<string, number>()
    if (!isAggregateScope) return counts
    for (const col of confirmRecordImportFlow.confirmRecordRefColumns) {
      const retailerSlots = resolveAggregateTargetSlots(col.id)
      if (retailerSlots !== undefined) counts.set(col.id, retailerSlots.length)
    }
    return counts
  }, [
    isAggregateScope,
    confirmRecordImportFlow.confirmRecordRefColumns,
    resolveAggregateTargetSlots,
  ])

  // --- §6 簡易予測の一括取込 ---

  const simpleForecastImportFlow = useSimpleForecastImportFlow({
    caseId,
    selectedAreaCode,
    section,
    targetDate,
    targetScope,
    selectedRetailerBusinessCd,
    editRows,
    applyImportedDraft,
    applyPreviewSimpleForecastBgResults,
    onPreviewApplied: ({ zeroedRetailerBusinessCds, sourceDates }) => {
      setSimpleForecastGapNotice(
        zeroedRetailerBusinessCds.length === 0
          ? undefined
          : {
              kind: 'simple',
              retailerNames: zeroedRetailerBusinessCds.map(
                (code) =>
                  editRows[code]?.displayName ??
                  retailers.find(
                    (retailer) => retailer.retailerBusinessCd === code,
                  )?.displayName ??
                  code,
              ),
              sourceDates: sourceDates.map(
                (date) => `${date.year}/${date.month}/${date.day}`,
              ),
            },
      )
    },
  })

  // 類似日検索ダイアログフロー (開閉状態 + 検索 + 初期選択解決)
  const similarDayFlow = useSimilarDayFlow({
    caseId,
    selectedAreaCode,
    targetDate,
    existingHeaderDates: pastActualDateStrs,
    // 過去実績1〜5列を時系列順で表示するため、適用時にソートする
    // (yyyy-MM-dd 文字列の Unicode 昇順 = 時系列昇順)
    onApplyDates: (dates) => setPastActualDateStrs([...dates].sort()),
  })

  // 実績トレンド反映ダイアログ
  const [isTrendReflectOpen, setIsTrendReflectOpen] = useState(false)

  // 表示 gate と 操作 gate、RPC 発火 gate を分離 (PR #5226 ikdene review 指摘 →
  // PR #5728 レビュー第2ラウンド指摘で RPC 発火 gate をさらに分離、spec 準拠):
  //   - trendReflectEnabled: 対象日=today かつページ読込済み。
  //     useTrendReflectionWindow (PreviewTrendReflection) の RPC 発火可否。
  //     既定窓 (6コマ) の GetDemandForecastDaily 結果が absent かどうかには
  //     依存させない — 保存済み W≠6 なら既定窓が absent でも W では有効実績
  //     ペアが揃い preview を返せることがあり、既定窓 absent で RPC 自体を
  //     止めるとその状況で追従できない (バッジ・W 入力欄が出ない bug)。
  //   - hasTrendReflect: 対象日=today かつ「最終的に採用する trendPreview」
  //     (hook 再計算 or GetPage 既定窓) が存在 (spec §3 = AC-DFD-009 /
  //     AC-DFD-013 表示条件)。乖離率バッジ・実績トレンド予測破線・「計画に反映」
  //     ボタンの共通表示 gate。 AC-DFD-013 は非表示条件を「実績コマ 2 件未満」
  //     に限定しており readonly は含めない = 読み取り専用中でも監視用の表示は残す
  //     (確定後も後続 step 進行中の当日監視で乖離率と実績トレンドが判断材料になる)。
  //   - canApplyTrendReflect: 表示 gate に加えて操作可否 (!isCompleted) を AND。
  //     AC-DFC-007 「編集・確定操作を無効化」に AC-DFD-012 実績トレンド反映が
  //     含まれるため、readonly 中はボタン + ダイアログ + apply handler を封鎖する。
  //     直前 fix (`hasTrendReflect` 単独に isCompleted を混ぜていた) は表示 gate も
  //     退行させて AC-DFD-009 破線・バッジまで消していた regression の分離。
  //
  // 集計窓 W が既定 (6コマ) の間、かつ既定窓の trend_preview が present の間は
  // GetDemandForecastDaily の trend_preview (既定窓で算出済み) をそのまま使う。
  // W が既定と異なる・変更された、または既定窓が absent の場合は
  // useTrendReflectionWindow が PreviewTrendReflection で再計算した値に
  // 差し替える (demand-trend-window-variable design D2, Contract「既定 6 コマ
  // 据置き・frontend が保持 W≠6 のとき本 RPC で追従」)。
  const pageTrendPreview = page?.trendPreview
  const trendReflectEnabled = isTargetDateToday && page !== undefined
  const trendWindow = useTrendReflectionWindow({
    caseId,
    areaCode: selectedAreaCode,
    targetDate,
    enabled: trendReflectEnabled,
  })
  const trendPreview = trendWindow.hasFetched
    ? trendWindow.trendPreview
    : pageTrendPreview
  const hasTrendReflect = trendReflectEnabled && trendPreview !== undefined
  // 操作可否 = 表示 gate + canEditNow (`canMutateTargetDate && !isCompleted`)。
  // hasTrendReflect は対象日=today を含意し today は過去日にならない
  // (canMutateTargetDate=true) ため、canEditNow は実質 !isCompleted と等価だが、
  // 散在していた `canMutateTargetDate && !isCompleted` の再 AND を page-model の
  // 名前付き gate に一本化する (AC-DFC-007 の readonly 封鎖と同一 SoT)。
  const canApplyTrendReflect = hasTrendReflect && canEditNow

  // 実績トレンド予測スロット (trendPreview.correctedSlots)
  const trendForecastKomaSlots = useMemo(() => {
    if (trendPreview === undefined || trendPreview.correctedSlots.length === 0)
      return undefined
    return protoSlotsToKoma(trendPreview.correctedSlots)
  }, [trendPreview])

  // 乖離率 (avg_ratio: 1.0 基準)
  const avgRatio = useMemo<number | null>(() => {
    const val = trendPreview?.avgRatio?.value
    if (val === undefined || val === '') return null
    const n = parseFloat(val)
    return isNaN(n) ? null : n
  }, [trendPreview?.avgRatio?.value])

  // 実績トレンド反映ダイアログ専用の live-basis 再計算
  // (demand-trend-reflection-single-authority design D2/D5): 「計画に反映」
  // 押下 (modal open) 時に live editRows を PreviewTrendReflection へ渡して
  // hold し、ダイアログの表示・Apply 対象系列はこの応答のみを唯一の source と
  // する (ambient trendPreview = saved 基準とは独立)。
  const trendReflectDialogPreview = useTrendReflectDialogPreview({
    caseId,
    areaCode: selectedAreaCode,
    targetDate,
    windowKoma: trendWindow.windowKoma,
    // preview 失敗時はダイアログを閉じて操作不能状態 (Apply 恒久 disabled・
    // 表示値 0) を残さない。error toast は hook が表示する。ユーザーは再度
    // 「計画に反映」で retry できる。
    onError: () => {
      setIsTrendReflectOpen(false)
    },
  })
  const dialogTrendPreview = trendReflectDialogPreview.preview
  const dialogAvgRatio = useMemo<number | null>(() => {
    const val = dialogTrendPreview?.avgRatio?.value
    if (val === undefined || val === '') return null
    const n = parseFloat(val)
    return isNaN(n) ? null : n
  }, [dialogTrendPreview?.avgRatio?.value])
  const dialogTotals = useMemo(() => {
    if (dialogTrendPreview === undefined) return undefined
    return computeTrendTotals(
      displaySlots,
      dialogTrendPreview.correctedSlots.map((s) => s.value?.value),
    )
  }, [dialogTrendPreview, displaySlots])

  // AC-DFD-006a-1:
  // チャート延長部の需要想定は表示中スコープの前日・翌日の業務確定から取る。小売は選択中
  // 小売の RetailerDraft の値、帰属不明は確定 record を持たないため無し、それ以外は
  // page の BG_TOTAL 値 (選択中小売が見つからない時は displaySlots と同じく BG全体を描く)。
  // 確定が無い側は空配列で届くので undefined (= 描かない) にする。
  const { preForecastKomaSlots, postForecastKomaSlots } = useMemo(() => {
    const selectedRetailer =
      targetScope === 'RETAILER'
        ? retailers.find(
            (r) => r.retailerBusinessCd === selectedRetailerBusinessCd,
          )
        : undefined
    const extension =
      targetScope === 'UNATTRIBUTED'
        ? undefined
        : selectedRetailer !== undefined
          ? selectedRetailer.extensionForecast
          : page?.bgTotalExtensionForecast
    const preSlots = extension?.preSlots ?? []
    const postSlots = extension?.postSlots ?? []
    return {
      preForecastKomaSlots:
        preSlots.length > 0 ? protoSlotsToKoma(preSlots, 2) : undefined,
      postForecastKomaSlots:
        postSlots.length > 0 ? protoSlotsToKoma(postSlots, 6) : undefined,
    }
  }, [targetScope, page, retailers, selectedRetailerBusinessCd])

  // ── 断面描画専用値型 (アーキ改善 候補7 Design Note D1〜D9) ──
  // index → ForecastCard/ForecastChart (chart 系列)・KomaCard/KomaGrid (48コマ表)
  // の素通しを 1 箇所に畳む。gate 済み系列 (todayActual は当日のみ /
  // trendForecast は hasTrendReflect のみ / currentKoma も当日のみ)、系列存在
  // 述語 (ForecastCard 凡例 / ForecastChart 描画可否の二重計算)、pastActuals の
  // FETCH_FAILED 降格 (D6)、同名別概念の baselineSlots 分離 (D1 前提・制約)、
  // 48コマ表の参照列 concat・adoptUnavailable 判定 (D7、slice 2) はすべて
  // builder 内の 1 箇所で計算する (models/forecastSectionView.ts 参照)。
  // forecastCard / komaCard object はいずれも view (この 1 インスタンス) を
  // そのまま渡すだけで、gate 済み field (todayActualSlots / currentKoma /
  // manualEditKomas / dirtyKomas / referenceColumns / isReadOnly /
  // isAggregateScope / adoptedReferenceColumnKey) も
  // gate を持たない素通しフィールド (gcKoma / displaySlots / baselineSlots) も
  // 個別 prop としては渡さない (D9: KomaCard/KomaGrid が view から destructure
  // する。komaCard.view → KomaGrid.view と同じ instance が forward される)。
  // NOTE: preTemperatureActualKomaSlots / preDewpointActualKomaSlots は上で
  // inline 変換しており参照が毎レンダー変わるため、page 取得後はこの memo は
  // 実質毎レンダー再計算される (refactor 前から同じ計算が毎レンダー走っていた
  // ため退行ではない)。両変換の useDemandForecastGridData への移設は follow-up
  // (PR #7016 参照)。view の等値比較を前提にした設計はこの解消後に行うこと。
  const forecastSectionView = useMemo(
    () =>
      buildForecastSectionView({
        targetScope,
        isTargetDateToday,
        displaySlots,
        currentEditRow,
        displayLabel,
        isEditableTarget: canImportToDraft,
        canEditAggregate,
        komaBaselineSlots,
        todayActualSlots: todayActualKomaSlots,
        temperatureForecastSlots: temperatureForecastKomaSlots,
        temperatureActualSlots: temperatureActualKomaSlots,
        dewpointActualSlots: dewpointActualKomaSlots,
        preTemperatureActualSlots: preTemperatureActualKomaSlots,
        preDewpointActualSlots: preDewpointActualKomaSlots,
        preForecastSlots: preForecastKomaSlots,
        postForecastSlots: postForecastKomaSlots,
        pastActuals: page?.pastActuals ?? [],
        pastActualsStatus,
        confirmRecordRefs: confirmRecordImportFlow.confirmRecordRefColumns,
        trendForecastSlots: trendForecastKomaSlots,
        hasTrendReflect,
        gcKoma,
        currentKoma: rawCurrentKoma,
        gateCloseFrontier,
        targetDate: protoDateToCivilDate(targetDate),
        pastActualGridData,
        effectiveDirtyKomas,
        isReadOnly,
        isAggregateScope,
        adoptedReferenceColumnKey: effectiveAdoptedReferenceColumnKey,
        aggregateAdoptTargetCounts,
        canEditNow,
        editRows,
      }),
    [
      targetScope,
      isTargetDateToday,
      displaySlots,
      currentEditRow,
      displayLabel,
      canImportToDraft,
      canEditAggregate,
      komaBaselineSlots,
      todayActualKomaSlots,
      temperatureForecastKomaSlots,
      temperatureActualKomaSlots,
      dewpointActualKomaSlots,
      preTemperatureActualKomaSlots,
      preDewpointActualKomaSlots,
      preForecastKomaSlots,
      postForecastKomaSlots,
      page?.pastActuals,
      pastActualsStatus,
      confirmRecordImportFlow.confirmRecordRefColumns,
      trendForecastKomaSlots,
      hasTrendReflect,
      gcKoma,
      rawCurrentKoma,
      gateCloseFrontier,
      targetDate,
      pastActualGridData,
      effectiveDirtyKomas,
      isReadOnly,
      isAggregateScope,
      effectiveAdoptedReferenceColumnKey,
      aggregateAdoptTargetCounts,
      canEditNow,
      editRows,
    ],
  )

  // --- UI ---
  // 選択中エリアの日本語ラベル (AC-01: 確定ダイアログのエリア確認表示 / 気象観測
  // 地点note と共有する単一算出箇所)
  const areaLabel =
    selectedAreaCode !== undefined
      ? convertAreaLabel(selectedAreaCode)
      : undefined

  if (caseId === undefined) {
    return (
      <DemandForecastDailyLayout showDailyCrumb={false}>
        <RegionLoading minHeight="200px" isLoading />
      </DemandForecastDailyLayout>
    )
  }

  // AC-WFW-040: workflowId のワークフローが見つからない (存在しない・他組織・
  // 形式不正)。対象日もエリアも決まらないので本体を出さず、パンくずにも無効な ID を
  // 出さない。本体の取得 (useGetDemandForecastDaily) は対象日がケース由来のため
  // この状態では発火しない。
  if (isWorkflowNotFound) {
    return (
      <DemandForecastDailyLayout showDailyCrumb={false}>
        <WorkflowNotFoundView />
      </DemandForecastDailyLayout>
    )
  }

  // 本体の取得が InvalidArgument (ケースの対象日・BG の不整合、地点 override 不正等。
  // ケース自体は取得できている) の時の専用エラー画面
  if (
    pageError !== null &&
    pageError !== undefined &&
    pageError.code === Code.InvalidArgument
  ) {
    return (
      <DemandForecastDailyLayout
        caseId={caseId}
        sectionSegment={sectionSegment}
        workflowId={wfCase?.workflowId}
      >
        <InvalidWorkflowIdView />
      </DemandForecastDailyLayout>
    )
  }

  if (
    pageError !== null &&
    pageError !== undefined &&
    pageError.code === Code.FailedPrecondition &&
    resolveGetDetailedErrorMessage(pageError, t) !== undefined
  ) {
    return (
      <DemandForecastDailyLayout
        caseId={caseId}
        sectionSegment={sectionSegment}
        workflowId={wfCase?.workflowId}
      >
        <ClientUpdateRequiredView />
      </DemandForecastDailyLayout>
    )
  }

  // A10: 業務エリア未設定 → 設定画面への誘導 (ハードエラーにしない)
  // ケース情報がロード済みで areaCode が UNSPECIFIED / 未定義の場合
  const isAreaNotSet = wfCase !== undefined && caseAreaCodes.length === 0
  if (isAreaNotSet) {
    return (
      <DemandForecastDailyLayout
        caseId={caseId}
        sectionSegment={sectionSegment}
        workflowId={wfCase?.workflowId}
      >
        <AreaNotSetView
          onGoToSettings={(e?: MouseEvent) => {
            openOrNavigate(navigate, routePaths.settings.path, e)
          }}
        />
      </DemandForecastDailyLayout>
    )
  }

  return (
    <DemandForecastDailyLayout
      caseId={caseId}
      notices={
        simpleForecastGapNotice !== undefined ? (
          <CompactAlert
            status="warning"
            title={
              simpleForecastGapNotice.kind === 'simple'
                ? t('page:demandForecastDaily.simpleForecast.gap.description', {
                    retailers: simpleForecastGapNotice.retailerNames.join(
                      t(
                        'page:demandForecastDaily.koma.aggregateAdoptMissingSeparator',
                      ),
                    ),
                    sourceDates: simpleForecastGapNotice.sourceDates.join(
                      t(
                        'page:demandForecastDaily.koma.aggregateAdoptMissingSeparator',
                      ),
                    ),
                  })
                : t(
                    simpleForecastGapNotice.kind === 'actual'
                      ? 'page:demandForecastDaily.koma.aggregateAdoptMissingActual'
                      : 'page:demandForecastDaily.koma.aggregateAdoptMissingConfirmed',
                    {
                      names: simpleForecastGapNotice.retailerNames.join(
                        t(
                          'page:demandForecastDaily.koma.aggregateAdoptMissingSeparator',
                        ),
                      ),
                      date: simpleForecastGapNotice.sourceDates.join(
                        t(
                          'page:demandForecastDaily.koma.aggregateAdoptMissingSeparator',
                        ),
                      ),
                    },
                  )
            }
          />
        ) : undefined
      }
      sectionSegment={sectionSegment}
      workflowId={wfCase?.workflowId}
    >
      <Stack gap={0} height="100%">
        {/* セクションヘッダー行。対象日ラベルに曜日・業務エリアを埋め込む
            (design L1764-1778): この画面は caseId 必須で Toolbar の業務エリア
            select を非表示化した代わりにここへ表示する。 */}
        <SectionHeader
          areaLabel={
            selectedAreaCode !== undefined
              ? convertAreaLabel(selectedAreaCode)
              : undefined
          }
          caseId={caseId}
          confirmedAt={wfCase?.demandConfirmedAt}
          confirmedBy={wfCase?.demandConfirmedBy}
          dayOfWeekLabel={dayOfWeekLabelJa(targetDate)}
          dayType={page?.targetDateDayType}
          demandForecastStatus={wfCase?.demandForecastStatus}
          sectionSegment={sectionSegment}
          showTargetDate={targetDate !== undefined}
          singularDayCategory={page?.singularDayCategory}
          singularDayName={page?.singularDayName}
          targetDateLabel={targetDateLabel}
          workflowId={wfCase?.workflowId}
        />

        {/* ツールバー (design 準拠: 業務エリアグループは非表示。予測プロバイダ切替
            チップは spec 準拠調整 (AC-07) で削除済み。AC-27: 類似日検索はトップ
            レベルボタン化済み) */}
        <Toolbar
          adjustmentDisabledReason={adjustmentDisabledReason}
          canRescale={canRescale}
          importActions={{
            canImport: canImportForecast,
            onConfirmRecordImportOpen:
              confirmRecordImportFlow.openConfirmRecordDialog,
          }}
          onRescaleOpen={openRescale}
          // AC-05: 未保存の変更がある状態での小売 / BG全体⇄小売切替は 3 択ダイアログ
          // 経由にする。ガード込みの handler は model が提供する。
          onRetailerChange={onRetailerChange}
          onSimilarDaySearchOpen={similarDayFlow.open}
          onTargetScopeChange={onTargetScopeChange}
          pastActual={{
            dates: pastActualDateStrs,
            onRemove: handlePastActualDateRemove,
            onDateChange: handlePastActualDateChange,
            // Decision #5: 過去実績 + 確定履歴の参照列 state をまとめて空にする
            onClear: handleClearReferenceColumns,
            confirmRecordRefs:
              confirmRecordImportFlow.confirmRecordRefColumns.map((c) => ({
                id: c.id,
                label: c.label,
              })),
            onConfirmRecordRefRemove:
              confirmRecordImportFlow.handleConfirmRecordRefRemove,
          }}
          retailers={retailers}
          selectedRetailerBusinessCd={selectedRetailerBusinessCd}
          simpleForecast={{
            methodValue: simpleForecastImportFlow.methodOptionValue,
            onMethodChange: simpleForecastImportFlow.onMethodChange,
          }}
          targetScope={targetScope}
        />

        {/* メインコンテンツ (チャートカード + 48コマ表カード + リサイザー) */}
        <DemandForecastWorkspace
          dragMode={dragMode}
          // AC-DFC-018 / 019: 確定済み表示の根拠記録 (design #dft-ro-reason)。
          forecastCard={{
            // 断面描画専用値型 (D1〜D6): 系列 10 個 + 存在述語 + 日付派生 +
            // 編集 gate はすべてここに畳んである (二重 gate の除去、上の
            // useMemo 参照)。
            view: forecastSectionView,
            // 補正モード (design L742-756): KomaGrid セル編集と同じ dragMode を
            // チャートドラッグ編集にも適用する
            dragMode,
            // AC-15: 48コマテーブルの列ホバーに対応するチャート強調
            highlightKoma: highlightedKoma,
            // AC-DFD-006a-6: 参照列見出しのホバーに対応する系列の強調・減光
            emphasizedSeriesKey,
            // AC-04/AC-05 (demand-forecast-danmen-guard): 外部凡例 (露点含む) の
            // hidden 状態を index.tsx から controlled で渡す (ForecastCard 側の
            // useState を撤去、Set 全体を持ち上げ済み)。
            hiddenKeys,
            onHiddenKeysChange: setHiddenKeys,
            onDragEdit: handleChartDragEdit,
            // 気象観測地点note (design L793-798): weather データの有無と独立に表示。
            // areaLabel は選択中エリアの日本語ラベル (SectionHeader と同じ helper)
            areaLabel,
            // placeholder 中は地点名を出さない (前の地点の名前が残るため)。
            observationPointName: isWeatherStale
              ? undefined
              : page?.observationPointName,
            // 気象観測地点の一時切替 (design demand/forecast-today.html
            // L1128-1200, AC-DFC-008a/008b)。
            weatherPointSwitch: weatherPointOverride,
            // D6: 確定/参照表示断面での一時切替 UI hide。hide predicate の SoT は
            // 上で計算済みの isCompleted (isReadonly ||
            // isDemandForecastReadonly(wfCase) の合成) — komaCard.isCompleted と
            // 同じ値をそのまま流用する。
            isCompleted,
            // 次のGC カウントダウン。 design は 2026-07-16 に page-header 右端から
            // カード上部へ移設し、 2026-07-17 に気象帯をグラフ直上へ動かした際も
            // 気象観測地点 note と同じ行のまま追従させている
            // (`demand/forecast-today.html` L1119-1125 のコメントに経緯あり)。
            // 実装は page-header (`headerExtra`) に取り残されていたため追随する
            // (デザイナー指摘 2026-08-17「8080 に準拠、 グラフの上辺り」、 #4858)。
            //
            // AC-01/AC-02 (spec AC-DFC-CD-001、 demand-forecast-danmen-guard):
            // 表示は計画断面 (対象日と今日の差で判定、AC-DFD-000) が当日のときだけ
            // (翌日/翌々日のケースは未来の計画作業をしている前提で GC 概念自体を出さない —
            // PR #4945 codex review 指摘)。 外側ゲートは sectionSegment (ケース読込前は
            // undefined で非表示)。
            // 対象日が壁時計 (JST) の今日と一致するかは GcCountdown 内部の
            // isTargetDateTodayInJst が live 抑止する (issue #4265 の残存経路対策。
            // targetDate は proto Date のまま渡す — non-JST ブラウザのタイムゾーン
            // ずれを避けるため fromProtoDate〈ローカル解釈〉を挟まない)。
            gcCountdown:
              sectionSegment === 'today' ? (
                <GcCountdown
                  gateCloseFrontier={gateCloseFrontier}
                  targetDate={targetDate}
                />
              ) : undefined,
            showThreeHourGranularityLimitNotice,
            showBeforeVpfdDailyNotice,
            // 実績トレンドの帯 (乖離率バッジ + 至近Nコマ + 「計画に反映」)。
            // 8080 (`.dft-trend-bar` = 凡例の直下) 準拠でツールバーから移設した。
            // gate の算出は上の hasTrendReflect / canApplyTrendReflect / avgRatio
            // をそのまま使う (移設で 1 つも変えていない)。
            trendBand: {
              hasTrendReflect,
              canApplyTrendReflect,
              // AC-DFD-013: trendPreview は実績 2 コマ未満の間は返らない (avgRatio=null)。
              isAwaitingActuals:
                trendReflectEnabled && avgRatio === null && canEditNow,
              avgRatio,
              windowKoma: trendWindow.windowKoma,
              onWindowKomaChange: trendWindow.onWindowKomaChange,
              onTrendReflectOpen: () => {
                // demand-trend-reflection-single-authority design D5: modal open
                // 時点の live editRows を live plan として PreviewTrendReflection
                // を発火する (modal が編集を block するので open〜apply 間で
                // live plan は不変)。
                trendReflectDialogPreview.open(editRows)
                setIsTrendReflectOpen(true)
              },
            },
            weatherBar: {
              // placeholder 中は気象を空にする (前の地点の値が新しいラベルの下に
              // 並ぶのを防ぐ)。 需要値は据え置く — 地点 override と無関係に
              // 同じ値が返るため (D2)。
              weather: isWeatherStale ? undefined : page?.weather,
              actualWeather: isWeatherStale ? undefined : page?.actualWeather,
              preActualHourlyWeather: isWeatherStale
                ? undefined
                : page?.preActualHourlyWeather,
              postActualHourlyWeather: isWeatherStale
                ? undefined
                : page?.postActualHourlyWeather,
              weatherStatus,
              // AC-DFC-020: 過去日は予報が無くても予報行・最高/最低気温を「—」で出す。
              isPastTargetDate: isTargetDateBeforeTodayInJst(targetDate),
            },
          }}
          header={
            showConfirmedRationale &&
            wfCase?.demandConfirmedAt !== undefined && (
              <ConfirmedRationaleCard
                confirmedAt={wfCase.demandConfirmedAt}
                confirmedBy={wfCase.demandConfirmedBy}
                rationale={page?.confirmRationale}
              />
            )
          }
          isLoading={isLoading}
          isRefreshing={isPlaceholderData}
          komaCard={{
            // AC-DFC-002a: signal 発火時はテーブル本体領域を BG 失効の案内表示に
            // 差し替える (KomaCard 側で他 prop を無視して案内のみ描画する)。
            bgExpiredNotice: isBgExpiredNotice
              ? { targetDateLabel }
              : undefined,
            // D9 (slice 2): displaySlots / baselineSlots / todayActualSlots /
            // gcKoma / currentKoma / pastActualSlots / adoptedReferenceColumnKey /
            // dirtyKomas / isReadOnly / isRetailerEditableTarget /
            // isTargetDateToday は view (forecastSectionView)
            // に集約済みのため個別 prop としては渡さない (KomaCard が view から
            // 読む)。
            view: forecastSectionView,
            displayLabel,
            // 編集可否決定は page-model の gate (forecastGates) に集約済み。KomaCard は
            // 再計算せず gate 導出値を消費する: canEditNow (`canMutateTargetDate &&
            // !isCompleted`、保存ボタン / インフォメッセージ gate)、
            // adjustmentDisabledReasonKey (採用ボタン非活性理由)。
            canEditNow,
            adjustmentDisabledReasonKey: adjustmentDisabledReasonKeyValue,
            onHoverKoma: setHighlightedKoma,
            onHoverSeriesKey: setEmphasizedSeriesKey,
            onKomaChange: handleKomaChange,
            onPaste: handlePaste,
            onDiscard: handleDiscardChangesWithGapClear,
            // AC-DFD-047/047a/047b, AC-DFD-048/048a: 参照列ヘッダー「採用」ボタン。
            // 押下中の同列再クリックは採用直前の値へ戻す (トグル、hook が判定)。
            // 集計スコープ (BG全体×配下複数) では per-retailer 採用へ routing する
            // (Decision 5/6、handleAdoptReferenceColumnRouted / 上記コメント参照)。
            onAdoptReferenceColumn: handleAdoptReferenceColumnRouted,
            // AC-DFD-004d: BG 全体ビュー + 配下複数で未入力の内訳がある時の理由表示と
            // 保存の非活性。直接編集可否 (AC-004c の配下 1 件解除 / AC-DFD-054 の
            // 全内訳入力済みでの解除) は view.editing.isReadOnly に gate 側で合成済み。
            aggregateUnfilledRetailerNames,
            anyDirty,
            // 共通仕様 AC-DFC-EDIT-002「変更を破棄」の活性条件。dirty は単一 seam
            // (useForecastDirtyState の currentRowDirty) から gate で導出済みの値を
            // 渡し、KomaCard 側での再計算をやめる (アーキ改善: dirty 導出を 1 seam に統一)。
            canDiscard,
            isSaving: hasInFlight,
            lastSavedAt,
            currentSaveState: effectiveSaveState,
            // AC-DFC-007 readonly gate 等の reason 別文言。effectiveSaveState と同じく
            // BG_TOTAL(複数小売) ビューでは undefined (= KomaCard 側で既存の code
            // ベース fallback 文言を表示)。
            saveErrorMessage: currentSaveErrorMessage,
            onSave: handleSave,
            onResolveConflict: handleResolveConflict,
          }}
          onDragModeChange={setDragMode}
        />

        {/* 確定バー (画面下部 sticky)。
            readonly (isCompleted) 中は非表示にせず disabled で描画する
            (= 操作不可を「消える」ではなく「グレーアウト」で伝える UX 判断、
            PR #5226 レビュー後の user 判断)。sibling operations
            (canRescale / canImportToDraft / canImportForecast / KomaCard 保存)
            は既に「表示 + disabled」で読み取り専用中を表現しており、確定バーだけ
            消えるのは非対称だった。 */}
        <ConfirmBar
          // conventions.md §GC ロック: 過去日は確定操作 (値変更を伴う) も封鎖し、
          // 確定済み断面も封鎖する。過去日封鎖 + 確定済み封鎖は page-model の
          // canEditNow (`canMutateTargetDate && !isCompleted`) に集約済み。
          // AC-DFC-004b: isDemandSubStepNotReached (サブステップ未到達) を追加で OR する。
          // AC-DFD-004d: BG 全体 (配下複数) で未入力の内訳がある間は集計表示からの
          // 確定も止め、理由を hint に文で出す。
          disabled={
            targetScope === 'UNATTRIBUTED' ||
            !canConfirm ||
            isConfirming ||
            !canEditNow ||
            isDemandSubStepNotReached ||
            isAggregateLocked
          }
          // AC-DFC-004b Decision #3: 未到達は確定済み等の他条件と別軸で併存しうる
          // (lifecycle 順で未到達を優先表示)。未到達でなければ理由文も tooltip もなし
          // (= 従来どおり hint テキストのみで理由を示す)。
          disabledReason={
            isDemandSubStepNotReached
              ? t('page:demandForecastDaily.confirm.stepNotReachedTooltip')
              : undefined
          }
          hint={
            hasInFlight
              ? t('page:demandForecastDaily.confirm.savingHint')
              : isAggregateLocked && canEditNow
                ? t('page:demandForecastDaily.koma.aggregateUnfilledHint', {
                    names: formatBreakdownNames(
                      aggregateUnfilledRetailerNames,
                      i18n.language,
                    ),
                    count: aggregateUnfilledRetailerNames.length,
                  })
                : editRowList.length > 0 && anyDirty
                  ? t('page:demandForecastDaily.confirm.notSavedHint')
                  : confirmedHintAt !== undefined
                    ? t('page:demandForecastDaily.confirm.confirmedHint', {
                        time: formatDateTimeFull(confirmedHintAt),
                      })
                    : undefined
          }
          isLoading={isConfirming}
          onClick={openConfirmDialog}
        />
      </Stack>

      {/* 確定ダイアログ (AC-01: エリア名・確定時刻の確認表示を追加) */}
      <ConfirmDemandDialog
        areaLabel={areaLabel}
        error={confirmError}
        isLoading={isConfirming}
        isOpen={isConfirmOpen}
        onCancel={closeConfirmDialog}
        onConfirm={(rationale) => {
          void handleConfirmExecute(rationale)
        }}
        targetDateLabel={targetDateLabel}
      />

      <UnattributedGuardDialog
        isOpen={isUnattributedGuardOpen}
        onClose={closeUnattributedGuard}
      />

      {/* 確定完了 結果モーダル (design L998-1020) */}
      <ConfirmResultDialog
        caseId={caseId}
        confirmedAt={confirmResultAt}
        isOpen={confirmResultAt !== undefined}
        onClose={closeConfirmResult}
        targetDateLabel={targetDateLabel}
        workflowId={wfCase?.workflowId}
      />

      {/* 類似日検索ダイアログ */}
      <SimilarDayDialog
        autoSelectUnavailableReason={
          showThreeHourGranularityLimitNotice
            ? t(
                'page:demandForecastDaily.similarDay.autoSelectUnavailableDayAfterTomorrow',
              )
            : undefined
        }
        candidates={similarDayFlow.candidates}
        initialSelected={similarDayFlow.initialSelected}
        isError={similarDayFlow.isError}
        isLoading={similarDayFlow.isLoading}
        isOpen={similarDayFlow.isOpen}
        lookbackDays={similarDayFlow.lookbackDays}
        onApply={similarDayFlow.onApply}
        onCancel={similarDayFlow.cancel}
        // spec `demand-forecast-daily.md` 「自動選択ロジック」 の縮退条件: 対象日の
        // 予想気温 (最高 / 最低 / 時系列) がひとつでも欠けると自動選択は実行しない。
        // 翌々日以降は時系列の予想気温が構造的に得られない (`jma-weather-data.md`
        // AC-WD-005 の担当日分担) ため、 押す前から成立しないと分かる。 同 spec は
        // この断面でのボタンの表示・非活性・案内を Des 判断としており、 8080 プロトの
        // 判断 (非表示にせず非活性 + 理由表示) に従う (issue #6720)。
        //
        // 判定はケースの断面 (WorkflowSection) ではなく **対象日** で行う (上の
        // isTargetDateToday / isTargetDateTomorrow /
        // showThreeHourGranularityLimitNotice と同じ配線様式。 ケースの断面は雛形由来で
        // 対象日オフセットと 1:1 対応しない)。 根拠データも 3 時間粒度案内と同じ AC-WD-005 なので、 述語も同じ
        // `isTargetDateAtOrAfterDayAfterTomorrowInJst` を使う。
        onLookbackDaysChange={similarDayFlow.onLookbackDaysChange}
        onRetry={similarDayFlow.retry}
      />

      {/* §5 確定履歴選択ダイアログ (AC-DFD-029/030/031)。1件選択して「取込」で
          追加の確認ダイアログを挟まず即座に参照列プレビューを実行する
          (取込確認の1段化)。 */}
      <ConfirmRecordImportDialog
        addedRecordIds={confirmRecordImportFlow.addedRecordIds}
        confirmRecords={confirmRecordImportFlow.confirmRecords}
        isAtLimit={confirmRecordImportFlow.isConfirmRecordRefsAtLimit}
        isError={confirmRecordImportFlow.isRecordsError}
        isImporting={confirmRecordImportFlow.isImporting}
        isLoading={confirmRecordImportFlow.isRecordsLoading}
        isOpen={confirmRecordImportFlow.isConfirmRecordDialogOpen}
        onClose={confirmRecordImportFlow.closeConfirmRecordDialog}
        // 過去日 gate (PR #5066 review C-6) はここでは不要:
        // main の refactor 後、§5 は「参照列」型 = PreviewConfirmRecord は
        // draft を書き換えない read-only preview なので値変更経路ではない。
        onImport={confirmRecordImportFlow.selectConfirmRecord}
        onRetry={confirmRecordImportFlow.retryRecords}
      />

      {/* §6 WMA (加重移動平均/同区分加重平均) 設定ダイアログ (simple-methods AC-005a/006a) */}
      <SimpleForecastSettingsDialog
        entries={simpleForecastImportFlow.entryDefaults}
        entryDefaultsErrorMessage={
          simpleForecastImportFlow.entryDefaultsErrorMessage
        }
        isLoading={simpleForecastImportFlow.isEntryDefaultsLoading}
        isOpen={simpleForecastImportFlow.isSettingsOpen}
        method={simpleForecastImportFlow.settingsMethod}
        onApply={simpleForecastImportFlow.applySettings}
        onClose={simpleForecastImportFlow.cancelSettings}
        onDateChange={simpleForecastImportFlow.onSettingsDateChange}
        onSourceDateViewMonthChange={
          simpleForecastImportFlow.onSourceDateViewMonthChange
        }
        targetSingularGroup={simpleForecastImportFlow.targetSingularGroup}
        targetWeekdayCategory={simpleForecastImportFlow.targetWeekdayCategory}
        unavailableSourceDates={simpleForecastImportFlow.unavailableSourceDates}
      />

      {/* §6 簡易予測 取込確認ダイアログ (AC-035: 手法名のみ、「予測エンジン」表現なし) */}
      <ImportConfirmDialog
        isLoading={simpleForecastImportFlow.isImporting}
        isOpen={simpleForecastImportFlow.isImportConfirmOpen}
        methodName={simpleForecastImportFlow.methodName}
        onClose={simpleForecastImportFlow.cancelImportConfirm}
        // 過去日の残存経路対策 (日跨ぎ) は編集 hook 側の gate が担う: §7 簡易予測は
        // PreviewSimpleForecast (read-only、書込みなし) で、応答は
        // applyImportedDraft / applyPreviewSimpleForecastBgResults 経由で draft
        // バッファへ反映されるが、これらは useForecastDraftEditor 内で過去日 no-op
        // になる (PR #5066 review C-1)。取込確認の disable gate は render 時の
        // canImportForecast (!canMutateTargetDate を含む) が第一防御。
        onConfirm={() => {
          void simpleForecastImportFlow.executeImportConfirm()
        }}
      />

      {/* 実績トレンド反映確認ダイアログ (操作系 = canApplyTrendReflect)。
          表示・Apply 対象系列は trendReflectDialogPreview (live-basis, hold
          済み) のみを source とする — main 画面の ambient trendPreview
          (saved 基準) とは独立 (design D2/D5)。 */}
      {canApplyTrendReflect && (
        <TrendReflectDialog
          afterTotalKwh={dialogTotals?.afterTotalKwh ?? 0}
          applicableKomaCount={dialogTrendPreview?.applicableKomaCount ?? 0}
          avgRatio={dialogAvgRatio}
          beforeTotalKwh={dialogTotals?.beforeTotalKwh ?? 0}
          isApplyDisabled={dialogTrendPreview === undefined}
          isApplyPending={trendReflectDialogPreview.isPending}
          isOpen={isTrendReflectOpen}
          onApply={() => {
            handleTrendReflectApply(
              dialogTrendPreview?.retailerCorrectedSlots ?? [],
            )
            setIsTrendReflectOpen(false)
            trendReflectDialogPreview.reset()
          }}
          onCancel={() => {
            setIsTrendReflectOpen(false)
            trendReflectDialogPreview.reset()
          }}
          windowKoma={trendWindow.windowKoma}
        />
      )}

      {/* 最大電力補正モーダル (共通仕様 AC-DFC-EDIT-001、AC-DFC-EDIT-003: 正の数〈小数可〉の kWh 入力) */}
      <RescaleModal
        currentPeakKwh={currentPeakKwh}
        isOpen={isRescaleOpen}
        // AC-DFC-EDIT-001 ※「締切済みコマ (GC 済みコマ) は変更対象外」。
        // gcKoma は「コマ 1〜gcKoma が GC 済み」の契約 (koma_number.go) なので
        // そのまま受給済みコマ数になる。
        lockedKomaCount={gcKoma}
        onApply={(targetPeakKwh) => {
          // Q3c/Q8: 保存値も kWh (送電端 kWh/30min、AC-DFC-011) なので入力値を
          // そのまま rescaleSlotsToPeak (handleRescaleApply 経由) に渡す。呼び出し
          // 境界 (useForecastDraftEditor.ts) は既存のまま維持する。
          handleRescaleApply(targetPeakKwh)
          closeRescale()
        }}
        onCancel={closeRescale}
      />

      {/* 未保存離脱ガードの 3 択ダイアログ (AC-05〜07): アプリ内ルート遷移 +
          画面内の編集対象切替の両方をここに合流させる。 */}
      <UnsavedLeaveDialog
        isOpen={leaveGuardBlocked}
        onCancel={leaveGuardCancel}
        onDiscardAndLeave={handleDiscardAndLeaveWithGapClear}
        onSaveAndLeave={() => void handleSaveAndLeave()}
        // 過去日は保存経路を封鎖する (conventions.md §GC ロック)。破棄経路は
        // KomaCard.canReset と同じく残しておき、日跨ぎ後の残存 dirty をユーザーが
        // 明示的に捨てられるようにする。判定式は canMutateTargetDate に統一。
        saveDisabled={!canMutateTargetDate || targetScope === 'UNATTRIBUTED'}
        saveDisabledReason={
          targetScope === 'UNATTRIBUTED'
            ? t('page:demandForecastDaily.leaveGuard.unattributedSaveDisabled')
            : undefined
        }
        saving={isLeaveSaving}
      />
    </DemandForecastDailyLayout>
  )
}
